import { useState } from 'react'

import {
  usePromotionOutboxDetailApi,
  usePromotionOutboxListApi
} from '@/common/api/modules/promotionOutbox'
import { useSiteId } from '@/common/app/site'
import { dateTimeStr } from '@/common/form/date'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  FIELD_SCHEMA,
  MODE_LABEL,
  OPERATION_LABEL,
  PROMO_META,
  STATUS_FLOW,
  STATUS_META
} from './promotionOutboxMeta'

const obId = (id: number) => `OB-${String(id).padStart(4, '0')}`

const StatusBadge = ({ status }: { status: string }) => {
  const m = STATUS_META[status] ?? { label: status, bg: '#EEF1F4', color: '#5A636E' }
  return (
    <span
      className='inline-block rounded-md px-2 py-0.5 text-[11px] font-bold'
      style={{ background: m.bg, color: m.color }}
    >
      {m.label}
    </span>
  )
}

// payload の値を表示用文字列へ
const fmt = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'はい' : 'いいえ'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

const th =
  'border-primary border px-3 py-2 text-left align-middle text-xs font-normal whitespace-nowrap'
const td = 'border-primary border px-3 py-2 align-middle'

// ステータスフィルタの選択肢
const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'すべて' },
  { value: 'draft', label: '下書き' },
  { value: 'approved', label: '承認済' },
  { value: 'queued', label: 'APIキュー投入' },
  { value: 'awaiting_manual', label: '手動登録待ち' },
  { value: 'registered', label: '登録済・同期待ち' },
  { value: 'verified', label: '実在確認済' },
  { value: 'failed', label: '失敗' },
  { value: 'canceled', label: '取消' }
]

const PromotionOutboxListPage = () => {
  const siteId = useSiteId()
  const [statusFilter, setStatusFilter] = useState('')
  const { data, isLoading } = usePromotionOutboxListApi({
    site_id: siteId,
    ...(statusFilter && { status: statusFilter })
  })
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const { data: detail, isLoading: detailLoading } = usePromotionOutboxDetailApi(
    selectedId ? { site_id: siteId, outbox_id: selectedId } : undefined
  )

  const rows = data?.items ?? []

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='プロモーション設定'
        description='プロモーションカルテが登録・変更する施策を、W2 反映前にまず保存する「書き込み前テーブル」です。APIキック／手動登録のどちらの経路でも同じテーブルを通るため、進捗を一元的に追跡できます。行をクリックすると登録内容の詳細を表示します。'
      />

      <Content>
        <div className='mt-3 flex items-center gap-3'>
          <Text size={12} weight='bold'>
            ステータス
          </Text>
          <select
            className='border-border-input rounded-md border px-3 py-1.5 text-sm'
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <Text size={12} color={TextColor.SECONDARY}>
            {data ? `${data.total}件` : ''}
          </Text>
        </div>

        <Area>
          {isLoading ? (
            <PageListSearching />
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full border-collapse text-sm'>
                <thead>
                  <tr className='bg-fill-surface-tertiary'>
                    <th className={th}>outbox_id</th>
                    <th className={th}>施策 / 登録機能 / 対象テーブル</th>
                    <th className={th}>操作</th>
                    <th className={th}>方式</th>
                    <th className={th}>ステータス</th>
                    <th className={th}>W2側ID</th>
                    <th className={th}>実在確認</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const pm = PROMO_META[r.promotion_type]
                    return (
                      <tr
                        key={r.id}
                        className='hover:bg-surface-secondary cursor-pointer'
                        onClick={() => setSelectedId(r.id)}
                      >
                        <td className={`${td} font-mono text-[12px]`}>{obId(r.id)}</td>
                        <td className={td}>
                          <div className='flex items-center gap-2'>
                            {pm && (
                              <span
                                className='grid h-7 w-7 flex-none place-items-center rounded-lg text-sm'
                                style={{ background: `${pm.color}22`, color: pm.color }}
                              >
                                {pm.icon}
                              </span>
                            )}
                            <span>
                              <span className='text-[12.5px] font-semibold'>
                                {pm?.name ?? r.promotion_type}
                              </span>
                              <span className='text-secondary'>
                                ／{r.function_name ?? r.function_key}
                              </span>
                              <br />
                              <span className='text-tertiary flex items-center gap-2 text-[10.5px]'>
                                <code className='font-mono'>{r.target_table}</code>
                                <span className='text-secondary'>{r.title}</span>
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className={td}>{OPERATION_LABEL[r.operation] ?? r.operation}</td>
                        <td className={td}>
                          {r.integration_mode
                            ? (MODE_LABEL[r.integration_mode] ?? r.integration_mode)
                            : '—'}
                        </td>
                        <td className={td}>
                          <StatusBadge status={r.status} />
                        </td>
                        <td className={`${td} font-mono text-[11.5px]`}>{r.w2_record_id ?? '—'}</td>
                        <td className={`${td} font-mono text-[11.5px]`}>
                          {r.verified_at ? dateTimeStr(r.verified_at) : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {rows.length === 0 && (
                <div className='py-10 text-center'>
                  <Text color={TextColor.SECONDARY}>該当する登録はありません</Text>
                </div>
              )}
            </div>
          )}
        </Area>

        <div className='flex flex-wrap items-center gap-2 text-[11px]'>
          <Text size={10} color={TextColor.SECONDARY}>
            ステータス遷移：
          </Text>
          {STATUS_FLOW.map((s, i) => (
            <span key={s} className='flex items-center gap-2'>
              {i > 0 && <span className='text-tertiary'>→</span>}
              <StatusBadge status={s} />
            </span>
          ))}
        </div>
      </Content>

      {/* 詳細モーダル */}
      {selectedId !== null && (
        <div
          className='z-modal fixed inset-0 flex items-center justify-center bg-black/60 p-4'
          onClick={() => setSelectedId(null)}
        >
          <div
            className='border-secondary bg-surface shadow-area flex max-h-[88vh] w-full max-w-2xl flex-col rounded-xl border'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='border-secondary flex items-center justify-between border-b px-5 py-3'>
              <Text size={16} weight='bold'>
                登録内容の詳細
              </Text>
              <button
                type='button'
                className='text-secondary hover:text-primary text-xl leading-none'
                onClick={() => setSelectedId(null)}
                aria-label='閉じる'
              >
                ×
              </button>
            </div>

            <div className='flex flex-col gap-4 overflow-auto px-5 py-4'>
              {detailLoading || !detail ? (
                <PageListSearching />
              ) : (
                <>
                  {/* 施策・登録機能・対象テーブル */}
                  <div className='flex flex-wrap items-center gap-2'>
                    {PROMO_META[detail.promotion_type] && (
                      <span
                        className='grid h-8 w-8 place-items-center rounded-lg text-base'
                        style={{
                          background: `${PROMO_META[detail.promotion_type].color}22`,
                          color: PROMO_META[detail.promotion_type].color
                        }}
                      >
                        {PROMO_META[detail.promotion_type].icon}
                      </span>
                    )}
                    <div>
                      <Text size={14} weight='bold'>
                        {detail.title || obId(detail.id)}
                      </Text>
                      <div className='text-secondary flex items-center gap-2 text-[11.5px]'>
                        <span>
                          {PROMO_META[detail.promotion_type]?.name ?? detail.promotion_type}
                        </span>
                        <span>／{detail.function_name ?? detail.function_key}</span>
                        <code className='text-tertiary font-mono'>{detail.target_table}</code>
                      </div>
                    </div>
                    <div className='ml-auto'>
                      <StatusBadge status={detail.status} />
                    </div>
                  </div>

                  {/* メタ情報 */}
                  <div className='bg-surface-secondary grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-lg px-4 py-3 text-[12px] sm:grid-cols-3'>
                    <div>
                      <span className='text-tertiary'>outbox_id：</span>
                      <span className='font-mono'>{obId(detail.id)}</span>
                    </div>
                    <div>
                      <span className='text-tertiary'>操作：</span>
                      {OPERATION_LABEL[detail.operation] ?? detail.operation}
                    </div>
                    <div>
                      <span className='text-tertiary'>方式：</span>
                      {detail.integration_mode
                        ? (MODE_LABEL[detail.integration_mode] ?? detail.integration_mode)
                        : '—'}
                    </div>
                    <div>
                      <span className='text-tertiary'>W2側ID：</span>
                      <span className='font-mono'>{detail.w2_record_id ?? '—'}</span>
                    </div>
                    <div>
                      <span className='text-tertiary'>実在確認：</span>
                      {detail.verified_at ? dateTimeStr(detail.verified_at) : '—'}
                    </div>
                    <div>
                      <span className='text-tertiary'>申請/承認：</span>
                      {(detail.requested_by ?? '—') + ' / ' + (detail.approved_by ?? '—')}
                    </div>
                  </div>

                  {detail.error && (
                    <div className='bg-surface-critical text-critical rounded-lg px-3 py-2 text-[12px]'>
                      エラー：{detail.error}
                    </div>
                  )}

                  {/* payload（施策別の登録内容） */}
                  <div>
                    <Text size={13} weight='bold'>
                      登録内容（{detail.target_table}）
                    </Text>
                    <div className='border-primary mt-2 overflow-hidden rounded-lg border'>
                      {(() => {
                        const schema = FIELD_SCHEMA[detail.function_key]
                        const p = detail.payload || {}
                        const shown = new Set<string>()
                        const defined = (schema ?? [])
                          .filter((f) => p[f.key] !== undefined)
                          .map((f) => {
                            shown.add(f.key)
                            return { label: f.label, value: p[f.key] }
                          })
                        const extra = Object.keys(p)
                          .filter((k) => !shown.has(k))
                          .map((k) => ({ label: k, value: p[k] }))
                        const all = [...defined, ...extra]
                        if (all.length === 0) {
                          return (
                            <div className='text-tertiary px-3 py-4 text-center text-[12px]'>
                              登録項目がありません
                            </div>
                          )
                        }
                        return all.map((row, i) => (
                          <div
                            key={row.label}
                            className={`grid grid-cols-[minmax(140px,38%)_1fr] gap-3 px-3 py-2 text-[12.5px] ${
                              i > 0 ? 'border-primary border-t' : ''
                            }`}
                          >
                            <span className='text-secondary'>{row.label}</span>
                            <span className='break-all'>{fmt(row.value)}</span>
                          </div>
                        ))
                      })()}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className='border-secondary flex justify-end border-t px-5 py-3'>
              <Button color={ButtonColor.SECONDARY} onClick={() => setSelectedId(null)}>
                閉じる
              </Button>
            </div>
          </div>
        </div>
      )}
    </Main>
  )
}

export default PromotionOutboxListPage
